"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Read-only",
    rule: "There is no Save, Edit, password change, or photo upload on this screen. Name, email, phone, and role come from Users (an admin edits them there)",
  },
  {
    topic: "Who sees it",
    rule: "Every signed-in user. Settings hub → My account → My profile. Page title My Profile",
  },
  {
    topic: "Incentive block",
    rule: "Total earned / Settled / Pending / Active schemes and the scheme list appear only when this user has at least one active scheme (activeSchemes > 0). Otherwise you only see the profile card",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. Your account card" },
      { heading: "2. Incentive earnings (when they exist)" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function MyProfilePage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            My Profile
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Every signed-in user — your own name, email, phone, and role
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            This is your personal account card. It does not set company GST,
            logo, or who can invite users. Those are{" "}
            <B>Settings → Profile</B> and <B>Settings → Users</B>.
          </p>
          <Flow
            steps={["Settings", "All settings", "My profile"]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Your account card
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title <B>My Profile</B>. A placeholder avatar, your name, and
            role underneath. Then Username (the same name), Email, Phone.
          </p>
          <Definitions
            items={[
              {
                term: "What it affects",
                desc: "Nothing in the books. It is a view of the logged-in user record so you can confirm who you are signed in as",
              },
              {
                term: "What it does not do",
                desc: "No edit, no password, no 2FA, no company details. An admin changes name, role, and status on Users",
              },
            ]}
          />
          <Screenshot caption="My Profile — name, role, Username, Email, Phone (read-only)" />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Incentive earnings (when they exist)
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            If you have at least one active incentive scheme, a second card
            appears.
          </p>
          <Definitions
            items={[
              {
                term: "Tiles",
                desc: "Total earned, Settled, Pending, Active schemes — amounts in the company base currency",
              },
              {
                term: "Scheme-wise incentive earned",
                desc: "Each scheme: name, earned, settled, pending, and a settled progress bar",
              },
              {
                term: "What it affects",
                desc: "Nothing you can post from here. It is a read of your incentive stats. Schemes themselves are created under Settings → Incentive schemes (admin)",
              },
            ]}
          />
          <Callout type="note">
            If you do not see this block, you have no active schemes. That is
            expected — not a missing permission on My Profile.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Company name, GSTIN, logo — not your user card
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/users"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Users <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where an admin changes name, role, and status
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
